// ─────────────────────────────────────────────────────────────────────────────
// DEVICE TIER DETECTION
// 3D (Three.js + Ammo.js cloth) is the PRIMARY render path on ALL devices.
// 2D canvas mode is emergency fallback only — when WebGL2 is completely absent.
// ─────────────────────────────────────────────────────────────────────────────

export type DeviceTier = "low" | "high";

export interface TierResult {
  tier: DeviceTier;
  reason: string;
  gpu: string;
  cores: number;
  memory: number; // GB, estimated
}

// GPU strings that are confirmed low-end / mobile-class
const LOW_END_GPU_PATTERNS = [
  /mali/i,
  /adreno [2-5]\d\d/i,
  /powervr/i,
  /sgx/i,
  /intel hd graphics [1-4]\d{3}/i,
  /intel.*hd.*4\d{2}/i,
  /apple a[1-9][^0-9]/i,  // A9 and below
  /radeon r[2-5]/i,
  /geforce [678]\d{2}[^m]/i,
];

// GPU strings confirmed capable of 3D cloth physics
const HIGH_END_GPU_PATTERNS = [
  /apple m[1-9]/i,
  /apple a1[0-9]/i,
  /adreno [67]\d\d/i,
  /mali-g[7-9]\d/i,
  /rtx/i,
  /rx [5-8]\d{3}/i,
  /radeon r[x9]/i,
  /geforce gtx 1[0-9]{3}/i,
  /geforce rtx/i,
];

function getWebGLRenderer(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      (canvas.getContext("webgl2") ||
        canvas.getContext("webgl") ||
        canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "none";

    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (ext) {
      return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
    }
    return gl.getParameter(gl.RENDERER) as string;
  } catch {
    return "unknown";
  }
}

function scoreGPU(renderer: string): 1 | 0 | -1 {
  if (HIGH_END_GPU_PATTERNS.some(p => p.test(renderer))) return 1;
  if (LOW_END_GPU_PATTERNS.some(p => p.test(renderer))) return -1;
  return 0; // unknown — fall back to other signals
}

function estimateMemoryGB(): number {
  // navigator.deviceMemory is non-standard but widely supported on Android/Chrome
  const nav = navigator as Navigator & { deviceMemory?: number };
  return nav.deviceMemory ?? 4; // default to 4GB if unknown
}

function getLogicalCores(): number {
  return navigator.hardwareConcurrency ?? 4;
}

function checkWebGL2(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!c.getContext("webgl2");
  } catch {
    return false;
  }
}

let _cached: TierResult | null = null;

export function detectDeviceTier(forceOverride?: DeviceTier): TierResult {
  if (_cached) return _cached;

  // ── Manual override (URL param for testing) ───────────────────────────────
  if (!forceOverride) {
    const param = new URLSearchParams(window.location.search).get("tier");
    if (param === "low" || param === "high") forceOverride = param;
  }

  const gpu     = getWebGLRenderer();
  const cores   = getLogicalCores();
  const memory  = estimateMemoryGB();
  const hasWGL2 = checkWebGL2();
  const gpuScore = scoreGPU(gpu);

  let tier: DeviceTier;
  let reason: string;

  if (forceOverride) {
    tier = forceOverride;
    reason = `manual override (?tier=${forceOverride})`;
  } else if (!hasWGL2) {
    // Only hard-block to 2D when the browser has zero WebGL2
    tier = "low";
    reason = "no WebGL2 — 2D fallback";
  } else {
    // 3D is primary on ALL devices that have WebGL2.
    // Even low-end GPUs (Mali, Adreno 3xx, Intel HD) get 3D —
    // the cloth sim degrades gracefully (PBD instead of Ammo when needed).
    tier = "high";
    reason = gpuScore === -1
      ? `3D primary (low-end GPU ${gpu} — PBD cloth fallback)`
      : gpuScore === 1
        ? `3D primary (high-end GPU: ${gpu})`
        : `3D primary (${memory}GB RAM, ${cores} cores)`;
  }

  _cached = { tier, reason, gpu, cores, memory };
  console.log(`[DeviceTier] ${tier.toUpperCase()} — ${reason}`);
  return _cached;
}

export function resetDeviceTierCache() {
  _cached = null;
}
